import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { toast } from 'react-toastify';

import { signIn } from 'actions/auth';
import Input from 'components/Form/Input';
import Validator from 'components/Validator/Validator';
import { Button, ButtonLink } from 'components/Button';
import { numberUnmask } from 'utils/masks';
import { resolveErrorMessage, clearAuthFlow } from 'utils/aws';

import {
  Container,
  Row,
  Form,
  Col,
  Card,
  CardHeader,
  CardBody,
  Alert,
} from 'reactstrap';
import { useConfig } from 'config/ConfigContext';

import schema from './loginSchema';
import './style.scss';

const Login = (props) => {
  const { isInMaintenance, massachusettsErrorPhone } = useConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const { from } = location.state || { from: { pathname: '/' } };
  const { validator: { values, errors } } = props;
  const [redirectToReferrer, setNavigateToReferrer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Reaching the sign-in screen ends any prior/abandoned auth or registration
  // flow, so drop the marker that lets the two-factor screens render. A fresh
  // sign-in / registration sets it again when a challenge or setup begins.
  useEffect(() => {
    clearAuthFlow();
  }, []);

  const handleInputChange = (event) => {
    const { validator: { onChangeHandler } } = props;
    const value = event.target.value.trim();
    onChangeHandler(event.target.name, value);
  };

  const handleSubmitFrom = (data, e) => {
    e.preventDefault();
    const { validator: { validate } } = props;

    if (isInMaintenance) {
      toast.error(
        (
          <div>
            <h5>The Customer Portal is currently under maintenance. Please come back later.</h5>
          </div>
        ),
        { autoClose: true }
      );
    } else if (validate(schema).isValid) {
      const formData = {
        ...data,
        username: `${data.username}`.toLowerCase(),
      };
      setIsLoading(true);
      props.signIn({
        data: formData,
        navigate,
        success: () => {
          setNavigateToReferrer(true);
          setIsLoading(true);
        },
        fail: (err) => {
          setIsLoading(false);
          const message = resolveErrorMessage(err);

          if (err.code === 'UserNotFoundException') {
            toast.error(
              (
                <div>
                  <h5>{message}</h5>
                </div>
              ),
              { autoClose: true }
            );
          } else if (err.code === 'NotAuthorizedException') {
            toast.error(
              (
                <div>
                  <h5>{message}</h5>
                </div>
              ),
              { autoClose: true }
            );
          } else if (err.code === 'PasswordResetRequiredException') {
            sessionStorage.setItem('reset-user', data.username);
            navigate('/forgot');
          } else {
            toast.error(
              (
                <div>
                  <h5>{message}</h5>
                </div>
              ),
              { autoClose: true }
            );
          }
        },
      });
    } else {
      console.log('api error');
    }
  };

  const forgotPassworrd = () => {
    navigate('/forgot');
  };

  const forgotUsername = () => {
    navigate('/forgot-username');
  };

  const registUser = () => {
    navigate('/registration');
  };

  const makePaymentNow = () => {
    navigate('/quickpay/self-identification');
  };

  return redirectToReferrer
    ? <Navigate to={from} />
    : (
      <>
        {
          isInMaintenance && (
            <Alert color="danger" className="text-center">
              The Customer Portal will be under maintenance from 3:00 AM to 5:00 AM (Pacific Time). We apologize for the inconvenience.
            </Alert>
          )
        }
        <div className="container new-design">
          <Container fluid>
            <Form onSubmit={handleSubmitFrom}>
              <Row className="align-items-center" style={{ minHeight: '500px' }}>
                <Col className="text-center">
                  <Row>
                    <Col className="sign-in-card" sm={{ size: 8, offset: 2 }} md={{ size: 6, offset: 3 }} lg={{ size: 5, offset: 0 }}>
                      <Card className="dark-shadow">
                        <CardHeader>Portal Account Sign In</CardHeader>
                        <CardBody>
                          <Row>
                            <Col>
                              <Input
                                name="username"
                                label="Username"
                                value={values.username}
                                onChange={handleInputChange}
                                hasError={!!errors.username}
                                hasValue
                                placeHolder="username"
                                isBadgeVisible={false}
                                isRequired
                                errorMessage={errors.username}
                              />
                            </Col>
                          </Row>
                          <Row>
                            <Col>
                              <Input
                                name="password"
                                type="password"
                                label="Password"
                                value={values.password}
                                onChange={handleInputChange}
                                hasError={!!errors.password}
                                hasValue
                                placeHolder="password"
                                isBadgeVisible={false}
                                isRequired
                                errorMessage={errors.password}
                              />
                            </Col>
                          </Row>
                          <Row>
                            <Col>
                              <Button
                                className={cn('w-100')}
                                onClick={handleSubmitFrom.bind(null, values)}
                                isLoading={isLoading}
                                color="primary"
                                type="submit"
                              >
                                Sign In
                              </Button>
                            </Col>
                          </Row>
                          <div className="sign-in-links">
                            <span className="sign-in-links-group">
                              <ButtonLink onClick={forgotPassworrd}>Forgot password</ButtonLink>
                              {' / '}
                              <ButtonLink onClick={forgotUsername}>username?</ButtonLink>
                            </span>
                            <ButtonLink onClick={registUser}>New user?</ButtonLink>
                          </div>
                          <div className="login-or-divider" aria-hidden="true">
                            <span>or</span>
                          </div>
                          <Button
                            className={cn('w-100', 'make-payment-now')}
                            color="primary"
                            type="button"
                            onClick={makePaymentNow}
                          >
                            Make a payment now
                          </Button>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Form>
          </Container>
        </div>
        <Alert color="danger" className="text-center" style={{ color: '#d82027', backgroundColor: '#fff6f6', borderColor: '#fff6f6' }}>
          Massachusetts residents, we are unable to receive payments through this site. Your payment should be made as provided in your monthly statement. If you have questions regarding your loan or want to make a payment, please call
          {' '}
          {numberUnmask(massachusettsErrorPhone) ? (
            <a href={`tel:+1${numberUnmask(massachusettsErrorPhone)}`}>{massachusettsErrorPhone}</a>
          ) : massachusettsErrorPhone}
        </Alert>
      </>
    );
};

Login.propTypes = {
  validator: PropTypes.object.isRequired,
  signIn: PropTypes.func.isRequired,
};

export default compose(
  Validator(schema),
  connect(
    (state) => ({
      auth: state.auth,
    }),
    {
      signIn,
    }
  )
)(Login);
